import DeviceData from 'drivers/deviceData';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import Services from 'services';
import { getCurrentLocale } from 'utils/i18n';
import AWSUrlProcessorService from 'services/awsUrlProcessor.service';
import KelvinToCelsius from 'utils/conversion/kelvinToCelsius';
import { isValidUrl } from 'utils/validations/url_validation';
import DeafultTimeZone from 'components/time/defaultTimeZone';
import environment from 'utils/environment';

/** Normalize BCP 47 locale — underscore tags (en_GB) throw on many Tizen WebViews */
function safeLocaleTag(locale) {
  if (!locale || typeof locale !== 'string') {
    return 'en-US';
  }
  return locale.replace(/_/g, '-');
}

/**
 * Location payloads use zone / timezone / city_id.timezone inconsistently across APIs.
 */
export function resolveLocationTimeZone(location) {
  if (!location) {
    return DeafultTimeZone.new_york;
  }
  return (
    location.zone ||
    location.timezone ||
    (location.city_id && location.city_id.timezone) ||
    DeafultTimeZone.new_york
  );
}

/**
 * Wall-clock Date for react-clock in a given IANA zone.
 * Avoids `new Date(toLocaleString(...))` which often yields Invalid Date on Tizen.
 */
export function getDateInTimeZone(date, timeZone) {
  var source = date instanceof Date ? date : new Date(date);
  if (isNaN(source.getTime())) {
    source = new Date();
  }
  var tz = timeZone || DeafultTimeZone.new_york;

  try {
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      var fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      var parts = fmt.formatToParts(source);
      var map = {};
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type !== 'literal') {
          map[parts[i].type] = parts[i].value;
        }
      }
      if (map.year && map.month && map.day && map.hour != null && map.minute != null) {
        return new Date(
          Number(map.year),
          Number(map.month) - 1,
          Number(map.day),
          Number(map.hour),
          Number(map.minute),
          Number(map.second || 0)
        );
      }
    }
  } catch (e) {
    // fall through
  }

  try {
    var legacy = new Date(source.toLocaleString('en-US', { timeZone: tz }));
    if (!isNaN(legacy.getTime())) {
      return legacy;
    }
  } catch (e2) {
    // fall through
  }

  return source;
}

export const getCurrentDateTime = async (timeZone = undefined) => {
  var dateTime;
  try {
    dateTime = await DeviceData.getCurrentDateTime();
  } catch (e) {
    console.warn('getCurrentDateTime: device clock failed, using JS Date', e && e.message);
    dateTime = new Date();
  }

  var deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST) || {};
  var isWEBOS = !!deviceList.isWEBOS;
  var time = '';
  var date = '';
  var LOCALE = safeLocaleTag(getCurrentLocale());

  if (!isWEBOS) {
    dateTime = new Date(dateTime);
    if (isNaN(dateTime.getTime())) {
      dateTime = new Date();
    }

    var city = Storages.MainStorage.getItem(StorageKeys.Keys.CURRENTLOCATION);
    var currentTimeZone = timeZone || resolveLocationTimeZone(city);

    try {
      date = dateTime.toLocaleDateString(LOCALE, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: currentTimeZone,
      });
      time = dateTime.toLocaleTimeString(LOCALE, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: currentTimeZone,
      });
    } catch (formatErr) {
      // Old Tizen / bad IANA zone / bad locale — still show local device time
      console.warn('getCurrentDateTime: locale format failed', formatErr && formatErr.message);
      try {
        date = dateTime.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
        time = dateTime.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      } catch (e2) {
        var h = dateTime.getHours();
        var m = dateTime.getMinutes();
        var ampm = h >= 12 ? 'PM' : 'AM';
        var h12 = h % 12 || 12;
        time = h12 + ':' + (m < 10 ? '0' : '') + m + ' ' + ampm;
        date =
          dateTime.getDate() +
          ' ' +
          dateTime.toDateString().split(' ')[1] +
          ' ' +
          dateTime.getFullYear();
      }
    }
  } else {
    time = [dateTime.localtime.hour, dateTime.localtime.minute].join(':');
    date = [dateTime.localtime.day, dateTime.localtime.month, dateTime.localtime.year].join('-');
  }
  return { date: date, time: time };
};

export const getWelcomeNoteData = () => {
  const welcomeNote = Storages.MainStorage.getItem(StorageKeys.Keys.WELCOMENOTE);
  const language_id = Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGEID);

  const locale_note = [];
  (welcomeNote ?? []).forEach((element) => {
    const locale_welcome_note = element.welcomenote_desc_locale.find(
      (local) => local.language_id === language_id
    );
    if (locale_welcome_note) {
      locale_note.push({
        welcome_note: element.title,
        locale_welcome_note: locale_welcome_note,
      });
    }
  });

  return locale_note;
};

export const parseWeatherDetails = (weatherData) => {
  const humidity = weatherData?.main?.humidity;
  const temp = KelvinToCelsius.kelvinToCelsius(weatherData?.main?.temp);
  const condition = weatherData?.weather?.[0]?.main;
  const icon = weatherData?.weather?.[0]?.icon;
  const windSpeed = weatherData?.wind?.speed;
  const name = weatherData?.name;
  return { temp, humidity, condition, icon, windSpeed, name };
};

export const getWeatherDetail = async (location, lang) => {
  let weatherApi = Storages.MainStorage.getItem(StorageKeys.Keys.WEATHERAPI) ?? null ;
  let licensekey = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY) ?? null;
  if(!weatherApi || !licensekey) {
    console.log('[info]','weather request blocked due to invalid weather api or license key')
    return
  }
  weatherApi += `&lang=${lang}&q=${location}`;
  const params = { url: weatherApi };  
  const weatherData = await Services.CallThirdPartyService.getCallThirdPartyService(params);

  return weatherData.data;
};

export const fetchRssFeed = async (description) => {
  if (description && isValidUrl(description)) {
    try {
      const params = {
        url: description,
        returnType: 'xml',
      };

      const response = await Services.CallThirdPartyService.getCallThirdPartyService(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching RSS feed:', error);
      throw error;
    }
  }
  return null;
};

export const trackPage = async (page) => {
  const body = { page_type: page };
  // await Services.TrackingService.postPageTracking({ body });
};

export const trackPageItem = async (page, item) => {
  const body = { page_type: page, item };
  // await Services.TrackingService.postItemTracking({ body });
};

/**
 * Backend media path prefixes returned by the API (CDN + resource endpoints).
 * App bundle paths (/static/media/ from webpack require()) are intentionally excluded.
 */
var BACKEND_MEDIA_PREFIXES = ['/cdn/', 'cdn/', '/resource/', 'resource/'];

function isBackendMediaPath(path) {
  var lower = path.toLowerCase();
  for (var i = 0; i < BACKEND_MEDIA_PREFIXES.length; i++) {
    if (lower.indexOf(BACKEND_MEDIA_PREFIXES[i]) === 0) {
      return true;
    }
  }
  return false;
}

/** Ensure a leading slash before joining with the API base URL. */
function normalizeBackendMediaPath(path) {
  return path.charAt(0) === '/' ? path : '/' + path;
}

/**
 * Turn backend CDN/API-relative paths into absolute URLs using the proxy/API base.
 *
 * Rewrites allowlisted prefixes only: /cdn/, cdn/, /resource/, resource/.
 * App bundle assets (e.g. webpack `/static/media/`) and other origin-relative paths
 * stay unchanged so they keep resolving against the app host in the browser and on Tizen WGT.
 *
 * Absolute http(s), protocol-relative (//), blob:, and data: URLs are returned unchanged.
 */
export function resolveImageBaseUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  var trimmed = url.trim();
  if (!trimmed) {
    return trimmed;
  }

  if (/^https?:\/\//i.test(trimmed) || /^blob:/i.test(trimmed) || /^data:/i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.indexOf('//') === 0) {
    return trimmed;
  }

  if (!isBackendMediaPath(trimmed)) {
    return trimmed;
  }

  var apiBase = environment.getApiURL();
  if (!apiBase) {
    return trimmed;
  }

  var path = normalizeBackendMediaPath(trimmed);
  var base = apiBase.charAt(apiBase.length - 1) === '/' ? apiBase.slice(0, -1) : apiBase;
  return base + path;
}

function appendApiKeyIfNeeded(processedUrl) {
  if (
    !processedUrl ||
    processedUrl.includes('apikey=') ||
    AWSUrlProcessorService.isObjectStorageUrl(processedUrl)
  ) {
    return processedUrl;
  }

  var apiKey = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY)?.api_key;
  if (!apiKey) {
    return processedUrl;
  }

  var separator = processedUrl.includes('?') ? '&' : '?';
  return processedUrl + separator + 'apikey=' + apiKey;
}

export const getImageAuthURL = (url) => {
  if (!url) {
    return '';
  }
  return appendApiKeyIfNeeded(resolveImageBaseUrl(url));
};

// Object-storage-aware version of getImageAuthURL (AWS S3 / Alibaba OSS / CareInn CDN).
// Pass `{ forceRefresh: true }` from on-error paths to bypass the pre-sign
// TTL cache and obtain a fresh signature.
export const getImageAuthURLAsync = async (url, options) => {
  if (!url) return '';

  var absoluteUrl = resolveImageBaseUrl(url);

  try {
    const processedUrl = await AWSUrlProcessorService.processUrl(absoluteUrl, options);
    return appendApiKeyIfNeeded(processedUrl);
  } catch (error) {
    console.error('Error processing image URL:', error);
    return getImageAuthURL(absoluteUrl);
  }
};
